import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";

// CSS Stylesheets
import "./KeyManage.css"; 

// Client Modules
import ManageClients from "./ManageClients";
import AddClients from "./AddClients";
import EditClients from "./EditClients";

// License Modules
import ManageLicense from "./ManageLicense";
import AddLicense from "./AddLicense";
import UpdateLicense from "./UpdateLicense";

// Servers Modules
import ManageServer from "./ManageServer";
import AddServer from "./AddServer";
import EditServer from "./EditServer";

// ==========================================
// 1. KMHOME (DASHBOARD) COMPONENT
// ==========================================
const KMHome = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalClients: 0,
    active: 0,
    expired: 0,
    trial: 0,
    online: 0,
    offline: 0,
  });

  const fetchStats = async () => {
    try {
      // LICENSE STATS
      const licRes = await axios.get(
        "https://streamsrvbac.n-tools.info/keymanage/license/stats"
      );

      // SERVER LIST (for online/offline calculation)
      const srvRes = await axios.get(
        "https://streamsrvbac.n-tools.info/keymanage/server/list"
      );

      const servers = srvRes.data || [];

      const online = servers.filter(
        (s) => s.status === "active"
      ).length;

      const offline = servers.filter(
        (s) => s.status !== "active"
      ).length;

      // CLIENT COUNT
      const cliRes = await axios.get(
        "https://streamsrvbac.n-tools.info/keymanage/clients/list"
      );

      setStats({
        totalClients: cliRes.data?.length || 0,
        active: licRes.data?.active || 0,
        expired: licRes.data?.expired || 0,
        trial: licRes.data?.trial || 0,
        online,
        offline,
      });

    } catch (err) {
      console.error("Stats error:", err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  return (
    <div className="key-manage-dashboard">
      {/* HEADER */}
      <div className="key-manage-header">
        <h1>Key Management Dashboard</h1>
      </div>

      {/* STATS CARDS */}
      <div className="key-manage-grid">
        <div className="key-manage-card key-manage-blue" onClick={() => navigate("clients")}>
          <h2>{stats.totalClients}</h2>
          <p>Total Clients</p>
        </div>

        <div className="key-manage-card key-manage-green" onClick={() => navigate("license")}>
          <h2>{stats.active}</h2>
          <p>Active Licenses</p>
        </div>

        <div className="key-manage-card key-manage-red" onClick={() => navigate("license")}>
          <h2>{stats.expired}</h2>
          <p>Expired Licenses</p>
        </div>

        <div className="key-manage-card key-manage-orange" onClick={() => navigate("license")}>
          <h2>{stats.trial}</h2>
          <p>Trial Licenses (3 Days)</p>
        </div>

        <div className="key-manage-card key-manage-cyan" onClick={() => navigate("manage-server")}>
          <h2>{stats.online}</h2>
          <p>Online Servers</p>
        </div>

        <div className="key-manage-card key-manage-gray" onClick={() => navigate("manage-server")}>
          <h2>{stats.offline}</h2>
          <p>Offline Servers</p>
        </div>
      </div>

      {/* QUICK ACTIONS Section Header */}
      <div className="key-manage-section-title">
        <h3>Quick Operations</h3>
        <p>Direct entry points for asset management</p>
      </div>

      {/* CARD STYLE ACTION BUTTONS WITH ICONS */}
      <div className="key-manage-actions-grid">
        
        {/* Manage Client Action Card */}
        <div className="key-manage-action-card client-action" onClick={() => navigate("clients")}>
          <div className="key-manage-action-icon-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <div className="key-manage-action-info">
            <h4>Manage Clients</h4>
            <p>View corporate accounts, customer contact records and registration listings.</p>
          </div>
          <span className="key-manage-action-arrow">→</span>
        </div>

        {/* Manage License Action Card */}
        <div className="key-manage-action-card license-action" onClick={() => navigate("license")}>
          <div className="key-manage-action-icon-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <div className="key-manage-action-info">
            <h4>Manage Licenses</h4>
            <p>Issue verification keys, track active tokens, filter expirations or freeze assets.</p>
          </div>
          <span className="key-manage-action-arrow">→</span>
        </div>

        {/* Manage Servers Action Card */}
        <div className="key-manage-action-card server-action" onClick={() => navigate("manage-server")}>
          <div className="key-manage-action-icon-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
          </div>
          <div className="key-manage-action-info">
            <h4>Manage Servers</h4>
            <p>Monitor distributed streaming relay endpoints, edge limits and cluster node pings.</p>
          </div>
          <span className="key-manage-action-arrow">→</span>
        </div>

      </div>
    </div>
  );
};


// ==========================================
// 2. MAIN KEYMANAGE (ROUTER) COMPONENT
// ==========================================
const KeyManage = () => {
  return (
    <div className="key-manage-main-wrapper">
      <Routes>
        {/* MAIN DASHBOARD */}
        <Route path="/" element={<KMHome />} />

        {/* CLIENT MODULE */}
        <Route path="clients" element={<ManageClients />} />
        <Route path="clients/add" element={<AddClients />} />
        <Route path="clients/edit" element={<EditClients />} />

        {/* LICENSE MODULE */}
        <Route path="license" element={<ManageLicense />} />
        <Route path="license/add" element={<AddLicense />} />
        <Route path="update-license" element={<UpdateLicense />} />

        {/* SERVER MODULE */}
        <Route path="manage-server" element={<ManageServer />} />
        <Route path="add-server" element={<AddServer />} />
        <Route path="edit-server" element={<EditServer />} />

        {/* FALLBACK */}
        <Route path="*" element={<Navigate to="" />} />
      </Routes>
    </div>
  );
};

export default KeyManage;